'use strict';

const SWISS_COUNTRY_CODE = 'CH';
const PRICING_SOURCE_CHANGED_EVENT = 'vinestock:pricing-source-changed';

/**
 * @typedef {{
 *   id: string,
 *   title?: string,
 *   noInternationalShipping?: boolean | string | number,
 *   price?: string,
 *   internationalPrice?: string
 * }} OfferConfig
 */

/**
 * Parse un tarif CMS en francs entiers.
 * Vide / whitespace / non-entier / négatif → undefined (aperçu masqué).
 * `'0'` → 0 (afficher CHF 0.-).
 *
 * @param {unknown} raw
 * @returns {number | undefined}
 */
function parseFrancs(raw) {
    if (raw === null || raw === undefined) {
        return undefined;
    }

    const trimmed = String(raw).trim();
    if (trimmed === '' || !/^\d+$/.test(trimmed)) {
        return undefined;
    }

    return Number.parseInt(trimmed, 10);
}

/**
 * @param {Element | null | undefined} root
 * @returns {OfferConfig[]}
 */
function parseOffersConfig(root) {
    const roots = [];

    if (root instanceof HTMLElement) {
        roots.push(root);
    }

    const documentRoot = document.querySelector(
        '[data-vine-stock-order-form], .vine-stock-order-form, .jf-site-content-vinestockorderform'
    );

    if (documentRoot instanceof HTMLElement && !roots.includes(documentRoot)) {
        roots.push(documentRoot);
    }

    for (const candidate of roots) {
        const raw = candidate.getAttribute('data-offers-config');
        if (!raw) {
            continue;
        }

        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed;
            }
        } catch {
            // Continuer avec les autres racines.
        }
    }

    return [];
}

/**
 * @param {HTMLSelectElement} field
 * @param {string} identifier
 * @returns {boolean}
 */
function matchesSelectField(field, identifier) {
    const suffix = `[${identifier}]`;

    if (field.name.endsWith(suffix)) {
        if (identifier === 'country' && field.name.endsWith('[recipientCountry]')) {
            return false;
        }

        return true;
    }

    if (identifier === 'country' && field.dataset.countryLockName?.endsWith(suffix)) {
        return true;
    }

    return false;
}

/**
 * @param {HTMLFormElement} form
 * @param {string} identifier
 * @returns {HTMLSelectElement | null}
 */
function findSelectField(form, identifier) {
    const fields = form.querySelectorAll('select');

    for (const field of fields) {
        if (!(field instanceof HTMLSelectElement)) {
            continue;
        }

        if (matchesSelectField(field, identifier)) {
            return field;
        }
    }

    return null;
}

/**
 * @param {OfferConfig[]} offers
 * @param {string} offerId
 * @returns {OfferConfig | null}
 */
function getOfferById(offers, offerId) {
    return offers.find((offer) => offer.id === offerId) ?? null;
}

/**
 * @param {OfferConfig | null | undefined} offer
 * @returns {boolean}
 */
function requiresSwissShippingOnly(offer) {
    if (!offer) {
        return false;
    }

    return (
        offer.noInternationalShipping === true ||
        offer.noInternationalShipping === 'true' ||
        offer.noInternationalShipping === 1 ||
        offer.noInternationalShipping === '1'
    );
}

/**
 * Miroir affichage de `VineStockOfferService::quote()` en francs CMS (pas des centimes).
 *
 * @param {OfferConfig | null} offer
 * @param {string} payerCountry
 * @returns {{ francs: number, certificateNote: boolean } | null}
 */
function quoteFrancs(offer, payerCountry) {
    if (!offer || payerCountry === '') {
        return null;
    }

    if (payerCountry === SWISS_COUNTRY_CODE) {
        const price = parseFrancs(offer.price);
        if (price === undefined) {
            return null;
        }

        return { francs: price, certificateNote: false };
    }

    if (requiresSwissShippingOnly(offer)) {
        return null;
    }

    const internationalPrice = parseFrancs(offer.internationalPrice);
    if (internationalPrice === undefined) {
        return null;
    }

    return { francs: internationalPrice, certificateNote: true };
}

/**
 * @param {HTMLElement} preview
 */
function hidePreview(preview) {
    preview.hidden = true;

    const value = preview.querySelector('[data-amount-preview-value]');
    const note = preview.querySelector('[data-amount-preview-note]');

    if (value) {
        value.textContent = '';
    }

    if (note instanceof HTMLElement) {
        note.hidden = true;
    }
}

/**
 * @param {HTMLElement} preview
 * @param {number} francs
 * @param {boolean} certificateNote
 */
function showPreview(preview, francs, certificateNote) {
    const value = preview.querySelector('[data-amount-preview-value]');
    const note = preview.querySelector('[data-amount-preview-note]');

    if (value) {
        value.textContent = `CHF ${francs}.-`;
    }

    if (note instanceof HTMLElement) {
        note.hidden = !certificateNote;
    }

    preview.hidden = false;
}

/**
 * Aperçu live du montant CHF sur S2. Enhancement JS uniquement :
 * le POST et `quote()` PHP restent la vérité.
 *
 * @param {Element | null} root
 * @param {HTMLFormElement} form
 */
export function initAmountPreview(root, form) {
    const preview = form.querySelector('[data-amount-preview]');
    if (!(preview instanceof HTMLElement)) {
        return;
    }

    const offers = parseOffersConfig(root instanceof HTMLElement ? root : null);
    const offerField = findSelectField(form, 'offer');
    const countryField = findSelectField(form, 'country');

    const update = () => {
        const offerId = offerField?.value ?? '';
        const payerCountry = countryField?.value ?? '';
        const offer = offerId ? getOfferById(offers, offerId) : null;
        const quoted = quoteFrancs(offer, payerCountry);

        if (!quoted) {
            hidePreview(preview);
            return;
        }

        showPreview(preview, quoted.francs, quoted.certificateNote);
    };

    offerField?.addEventListener('change', update);
    countryField?.addEventListener('change', update);
    form.addEventListener(PRICING_SOURCE_CHANGED_EVENT, update);
    update();
}
